@use '@/styles/tokens' as *;

/* Header cluster (rendered inside the drawer's cream title band) */
.header {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
}

.iconBadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: $color-brand-700;
  color: var(--text-inverse);
}

.headerText {
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
}

.headerHeading {
  font-family: $font-family-base;
  font-size: 18px;
  font-weight: $font-weight-medium;
  letter-spacing: -0.02em;
  color: $color-neutral-900;
}

.headerSubtitle {
  font-size: 13px;
  color: $color-neutral-950;
}

/* Order metadata card: 2×2 grid with per-row vertical dividers */
.infoCard {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: var(--space-5);
  row-gap: var(--space-4);
  margin: 0 0 var(--space-5);
  padding: var(--space-4);
  background: var(--surface-subtle);
  border-radius: 12px;
  border: 1px solid var(--border-subtle);
}

.infoItem {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;

  /* Left-column cells (1st, 3rd) carry the divider to the right column. */
  &:nth-child(odd) {
    padding-inline-end: var(--space-5);
    border-inline-end: 1px solid $color-neutral-200;
  }
}

.infoLabel {
  color: $color-neutral-600;
  font-size: 13px;
}

.infoValue {
  color: $color-neutral-950;
  font-size: 14px;
  /* Server free text (address). Same guard as `.rejectionValue` below, for the same reason. */
  overflow-wrap: break-word;
}

.sectionTitle {
  margin: 0 0 var(--space-3);
  color: $color-neutral-950;
  font-size: 14px;
  font-weight: 400;
}

.devices {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin: 0 0 var(--space-5);
  padding: 0;
  list-style: none;
}

.device {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-3);
  border: 1px solid $color-neutral-200;
  border-radius: 12px;
  background: none;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: $color-neutral-300;
    background: $color-neutral-50;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
}

.thumb {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  object-fit: cover;
  background: $color-neutral-100;
  flex: 0 0 auto;
}

.deviceInfo {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.deviceName {
  overflow: hidden;
  color: $color-neutral-900;
  font-size: 15px;
  font-weight: $font-weight-medium;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.deviceSubtitle {
  color: $color-neutral-500;
  font-size: 13px;
}

/* Rejected banner: Figma's red error treatment wrapping the actual rejection reason (ADR-0018) */
.rejection {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: 0 0 var(--space-5);
  padding: var(--space-4);
  background: $color-error-50;
  border-radius: 12px;
}

.rejectionHeading {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: $color-stage-attention-text;
  font-size: 14px;
  font-weight: $font-weight-regular;
}

.rejectionValue {
  margin: 0;
  color: $color-error-950;
  font-size: 12px;
  font-weight: $font-weight-regular;
  /* The rejection reason is admin-typed free text with no length or whitespace guarantee. The
     shared overlay body sets `overflow-y: auto`, which computes `overflow-x` to `auto` as well, so
     one unbroken 200-character token does not clip — it makes the whole drawer body scroll
     sideways, carrying every other card off screen with it (ADO 119717, QA 2026-09-22). Breaking
     the word is what every sibling request drawer already does with its own free-text fields
     (`replacement-detail-drawer`, `warranty-detail-drawer`); this one was the omission. */
  overflow-wrap: break-word;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
}
